import React, { useRef, useState } from 'react';
import { FileUp, Loader2, UploadCloud } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'csv', 'pdf', 'jpg', 'jpeg', 'png'];

export const PolicyUploadPanel = ({ uploading, progress, onUpload }) => {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ policyName: '', policyOwner: '', policyVersion: '' });
  const [file, setFile] = useState(null);
  const [validationError, setValidationError] = useState('');

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    setValidationError('');
    if (!selected) return setFile(null);
    const extension = selected.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      event.target.value = '';
      setFile(null);
      return setValidationError('Choose a DOC, DOCX, XLS, XLSX, CSV, PDF, JPG, JPEG, or PNG file.');
    }
    if (selected.size > MAX_FILE_BYTES) {
      event.target.value = '';
      setFile(null);
      return setValidationError('The attachment must be 15 MB or smaller.');
    }
    setFile(selected);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setValidationError('');
    if (!file) return setValidationError('Please select a policy attachment.');
    const succeeded = await onUpload({ ...form, file });
    if (succeeded) {
      setForm({ policyName: '', policyOwner: '', policyVersion: '' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <section className="dashboard-glass rounded-xl p-5 sm:p-6" data-testid="policy-upload-panel">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="dashboard-eyebrow">Controlled upload</p>
          <h2 className="mt-2 font-heading text-xl font-medium text-white" data-testid="policy-upload-title">Add policy document</h2>
          <p className="mt-2 text-sm leading-6 text-slate-400">Store approved policies and supporting documents in your private Pro workspace.</p>
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-emerald-400/25 bg-emerald-400/10"><UploadCloud className="h-5 w-5 text-emerald-300" /></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="policy-upload-form">
        <div>
          <Label htmlFor="policy-name" className="text-slate-300">Policy Name</Label>
          <Input id="policy-name" value={form.policyName} onChange={(event) => setForm((current) => ({ ...current, policyName: event.target.value }))} minLength={2} maxLength={160} required disabled={uploading} placeholder="Information Security Policy" className="mt-2 h-11 border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-emerald-400" data-testid="policy-name-input" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="policy-owner" className="text-slate-300">Policy Owner</Label>
            <Input id="policy-owner" value={form.policyOwner} onChange={(event) => setForm((current) => ({ ...current, policyOwner: event.target.value }))} minLength={2} maxLength={120} required disabled={uploading} placeholder="CISO / Security Team" className="mt-2 h-11 border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-emerald-400" data-testid="policy-owner-input" />
          </div>
          <div>
            <Label htmlFor="policy-version" className="text-slate-300">Policy Version</Label>
            <Input id="policy-version" value={form.policyVersion} onChange={(event) => setForm((current) => ({ ...current, policyVersion: event.target.value }))} minLength={1} maxLength={40} required disabled={uploading} placeholder="v1.0" className="mt-2 h-11 border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-emerald-400" data-testid="policy-version-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="policy-attachment" className="text-slate-300">Attachment</Label>
          <div className="mt-2 rounded-lg border border-dashed border-white/15 bg-slate-950/35 p-4 transition-colors focus-within:border-emerald-400/40">
            <Input ref={fileInputRef} id="policy-attachment" type="file" accept=".doc,.docx,.xls,.xlsx,.csv,.pdf,.jpg,.jpeg,.png" onChange={handleFileChange} required disabled={uploading} className="h-auto border-0 p-0 text-slate-300 shadow-none file:mr-4 file:rounded-md file:bg-white/10 file:px-3 file:py-2 file:text-slate-100 hover:file:bg-white/15 focus-visible:ring-0" data-testid="policy-attachment-input" />
            <p className="mt-3 text-xs text-slate-500" data-testid="policy-file-guidance">DOC, DOCX, XLS, XLSX, CSV, PDF, JPG, JPEG, or PNG · Maximum 15 MB</p>
          </div>
        </div>

        {validationError && <div className="rounded-lg border border-red-400/25 bg-red-400/10 px-3 py-2 text-sm text-red-200" role="alert" data-testid="policy-upload-validation-error">{validationError}</div>}
        {uploading && (
          <div data-testid="policy-upload-progress">
            <div className="mb-2 flex justify-between text-xs text-slate-400"><span>Uploading securely</span><span>{progress}%</span></div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-emerald-400 transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
          </div>
        )}
        <Button type="submit" disabled={uploading} className="h-11 w-full bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400" data-testid="policy-upload-submit-button">
          {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading document</> : <><FileUp className="mr-2 h-4 w-4" />Save policy document</>}
        </Button>
      </form>
    </section>
  );
};